import React, { useState, useEffect, useRef } from "react";
import { IconButton } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseIcon from "@mui/icons-material/Pause";
import { Modal, Button } from "react-bootstrap";
import CheckoutForm from "../Form/CheckoutForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import "../../assets/stylesheets/CourseDetails.css";
import { fetchData, createData } from "../../api/api";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function CourseDetails({ course, handleSnackbar }) {
  const [clientSecret, setClientSecret] = useState("");
  const [playing, setPlaying] = useState(false);
  const [currentSessionIndex, setCurrentSessionIndex] = useState(null);
  const [completedExercises, setCompletedExercises] = useState({});
  const [audioUrl, setAudioUrl] = useState("");
  const audioRef = useRef(null);
  const [savedProgress, setSavedProgress] = useState(null); // { exerciseIndex, position, label }
  const progressStorageKey = `audio_progress_course_${course.id}`;
  const [hasAccess, setHasAccess] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const handleCloseModal = () => setShowModal(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [couponCode, setCouponCode] = useState("");

  useEffect(() => {
    const fetchCompletedExercises = async () => {
      try {
        const completedExerciseIds = await fetchData("listenings/" + course.id);
        setCompletedExercises(new Set(completedExerciseIds));
      } catch (error) {
        console.error("Error fetching completed exercises:", error);
      }
    };

    fetchCompletedExercises();

    // Load saved progress from localStorage
    try {
      const saved = localStorage.getItem(progressStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        const exercise = course.exercises[parsed.exerciseIndex];
        if (exercise && parsed.position > 0) {
          setSavedProgress({
            exerciseIndex: parsed.exerciseIndex,
            exerciseId: parsed.exerciseId,
            position: parsed.position,
            label: exercise.name,
          });
        }
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const response = await fetchData(`programs/has_access/${course.id}`);
        setHasAccess(response.hasAccess);
      } catch (error) {
        console.error("Course access not allowed...", error);
      }
    };
    checkAccess();
  }, [course]);

  const updateAccessStatusAndCloseModal = (hasAccess) => {
    setHasAccess(hasAccess);
    handleCloseModal();
  };

  // When audioUrl changes (new exercise selected), load and play via ref
  useEffect(() => {
    if (!audioUrl || !audioRef.current) return;
    const audio = audioRef.current;
    audio.load();

    const onLoaded = () => {
      // Restore saved position if this is the saved exercise
      const saved = (() => {
        try { return JSON.parse(localStorage.getItem(progressStorageKey)); } catch { return null; }
      })();
      if (saved && saved.exerciseIndex === currentSessionIndex && saved.position > 0) {
        audio.currentTime = saved.position;
      }
      audio.play().catch(() => {});
    };
    audio.addEventListener("loadedmetadata", onLoaded, { once: true });
    return () => audio.removeEventListener("loadedmetadata", onLoaded);
  }, [audioUrl]);

  // Save progress to localStorage every 5 seconds while playing
  const handleTimeUpdate = () => {
    if (!audioRef.current || currentSessionIndex === null) return;
    const exercise = course.exercises[currentSessionIndex];
    if (!exercise || audioRef.current.currentTime < 1) return;
    localStorage.setItem(progressStorageKey, JSON.stringify({
      exerciseIndex: currentSessionIndex,
      exerciseId: exercise.id,
      position: audioRef.current.currentTime,
    }));
  };

  const resumeSavedProgress = async () => {
    if (!savedProgress) return;
    const exercise = course.exercises[savedProgress.exerciseIndex];
    if (!exercise) return;
    await playAudio(exercise, savedProgress.exerciseIndex);
    setSavedProgress(null);
  };

  const togglePlayPause = async (exercise, index) => {
    if (currentSessionIndex === index) {
      // Same exercise: toggle play/pause via ref without remounting
      if (playing) {
        audioRef.current?.pause();
      } else {
        audioRef.current?.play().catch(() => {});
      }
    } else {
      await playAudio(exercise, index);
    }
  };

  const playAudio = async (exercise, index) => {
    const completedArray = Array.from(completedExercises);
    if (
      course.orderRequired == "true" &&
      index > 0 &&
      !completedArray[index - 1]
    ) {
      alert("Bitte beenden Sie die vorherige Einheit, um fortzufahren.");
      return;
    }
    const response = await fetchData(
      `exercises/stream/${course.id}/${exercise.id}`
    );
    setCurrentSessionIndex(index);
    setAudioUrl(response.url);
  };

  const handleApplyCoupon = async () => {
    try {
      const response = await createData("payments/apply_coupon", {
        program_id: course.id,
        coupon_code: couponCode,
      });
      if (response.id) {
        updateAccessStatusAndCloseModal(true);
        handleSnackbar("Code erfolgreich angewendet!", "success");
      } else {
        console.error("Invalid coupon code");
        handleSnackbar("Ungültiger Code", "error");
      }
    } catch (error) {
      console.error("Error applying coupon code:", error);
      handleSnackbar("Fehler bei der Verwendung des Codes", "error");
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await fetchData(
        `program/download_attachment/${course.id}/${file.id}`
      );
      const url = response.url;
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const startCourse = () => {
    if (course.exercises.length > 0) {
      playAudio(course.exercises[0], 0);
    }
  };

  const handleAudioEnded = async () => {
    try {
      const exercise_id = course.exercises[currentSessionIndex].id;
      const program_id = course.id;
      const response = await createData("/listenings", {
        program_id,
        exercise_id,
      });
      if (response.exercise_id) {
        setCompletedExercises(
          new Set([
            ...completedExercises,
            course.exercises[currentSessionIndex].id,
          ])
        );
      } else {
        console.error("Error - Kann nicht abgeschlossen werden", response);
      }
    } catch (error) {
      console.error("Error2 - Kann nicht abgeschlossen werden", error);
    }

    // Clear saved progress for this exercise since it's completed
    localStorage.removeItem(progressStorageKey);
    setSavedProgress(null);
    setPlaying(false);
    setCurrentSessionIndex(null);
  };

  const handleOpenModal = async () => {
    try {
      const response = await createData("payments/create_payment_intent", {
        program_id: course.id,
      });
      if (response.clientSecret) {
        // const data = await response.json();
        setClientSecret(response.clientSecret);
      } else {
        console.error("Failed to fetch client secret");
      }
    } catch (error) {
      console.error("Error fetching client secret:", error);
    }
    setShowModal(true);
  };

  const renderCourseAction = () => {
    // Check if the user has access and the course is not playing
    if (!hasAccess) {
      return (
        <>
          <div className="course-price">Price: {course.price}</div>
          <Button variant="primary" onClick={handleOpenModal}>
            Kurs kaufen
          </Button>

          <Modal show={showModal} onHide={handleCloseModal}>
            <Modal.Header closeButton>
              <Modal.Title>Kurs: {course.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <CheckoutForm
                    onPaymentSuccess={() =>
                      updateAccessStatusAndCloseModal(true)
                    }
                    handleSnackbar={handleSnackbar}
                  />
                </Elements>
              ) : (
                <p>Zahlungsdetails werden geladen....</p>
              )}
            </Modal.Body>
            <Modal.Footer>
              <div className="coupon-container">
                <input
                  type="text"
                  placeholder="oder Code eingeben"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  className="coupon-input"
                />
                <Button variant="primary" onClick={handleApplyCoupon}>
                  Einlösen
                </Button>
              </div>
              <div className="close-button-container">
                <Button variant="secondary" onClick={handleCloseModal}>
                  Schließen
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        </>
      );
    } else {
      const formatTime = (secs) => {
        const m = Math.floor(secs / 60);
        const s = Math.floor(secs % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
      };
      return (
        <>
          {savedProgress && currentSessionIndex === null && (
            <div className="resume-banner" onClick={resumeSavedProgress}>
              <PlayCircleOutlineIcon style={{ marginRight: 6 }} />
              Weiter hören: <b style={{ margin: "0 4px" }}>{savedProgress.label}</b>
              ab {formatTime(savedProgress.position)}
            </div>
          )}
          {currentSessionIndex === null && course.exercises.length > 0 && (
            <button className="download-button" onClick={startCourse}>
              <PlayCircleOutlineIcon />
              KURS STARTEN
            </button>
          )}
          <div
            onContextMenu={(e) => e.preventDefault()}
            style={{
              userSelect: "none",
              display: currentSessionIndex !== null ? "block" : "none",
            }}
          >
            <audio
              ref={audioRef}
              src={audioUrl}
              controls
              onEnded={handleAudioEnded}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onTimeUpdate={handleTimeUpdate}
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>
        </>
      );
    }
  };

  return (
    <div
      className="course-details-container"
      style={{ backgroundColor: course.color }}
    >
      <div className="course-details-header">
        <h1>{course.name}</h1>
        <p>{course.description}</p>
        {/* <p className="course-instructor">von {course.instructor}</p> */}
      </div>

      <div className="course-action">
        {/* {playing ? (
          <audio
            src={audioUrl}
            autoPlay
            controls
            onEnded={handleAudioEnded}
            controlsList="nodownload"
          />
        ) : (
          <button className="download-button" onClick={startCourse}>
            <PlayCircleOutlineIcon />
            KURS STARTEN
          </button>
        )} */}
        {renderCourseAction()}
      </div>
      {course.attachments && course.attachments.length > 0 && (
        <div className="attachments-section">
          <div
            className="section-header"
            onClick={() => setShowAttachments(!showAttachments)}
          >
            <b>Anhang</b>
            <IconButton>
              {showAttachments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>
          {showAttachments && (
            <div className="attachments-list">
              {course.attachments.map((attachment, index) => (
                <div key={index} className="attachment-item">
                  <a
                    href={attachment.url}
                    download
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownload(attachment);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <i>{attachment.name}</i>
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {course.links && course.links.length > 0 && (
        <div className="links-section">
          <div
            className="section-header"
            onClick={() => setShowLinks(!showLinks)}
          >
            <b>Links</b>
            <IconButton>
              {showLinks ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>
          {showLinks && (
            <div className="links-list">
              {course.links.map((link, index) => (
                <div key={index} className="link-item">
                  <a href={link} target="_blank" rel="noopener noreferrer">
                    <i>{link}</i>
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="course-sessions">
        <h2>{course.unit_name}</h2>
        {course.exercises.length === 0 ? (
          <div style={{ marginTop: "30px", textAlign: "center" }}>
            <p>Es wurden keine Übungen zu diesem Kurs hinzugefügt...</p>
          </div>
        ) : (
          course.exercises.map((exercise, index) => (
            <div
              className={`session-item ${
                completedExercises[index] ? "completed" : ""
              }`}
              key={index}
            >
              <span className="session-index">{index + 1}te</span>
              <span className="session-title">{exercise.name}</span>
              {hasAccess && (
                <IconButton
                  className="session-icon"
                  onClick={() => togglePlayPause(exercise, index)}
                  disabled={!hasAccess}
                >
                  {currentSessionIndex === index && playing ? (
                    <PauseIcon />
                  ) : (
                    <PlayArrowIcon />
                  )}
                </IconButton>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default CourseDetails;
